import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService } from './api.service';
import { LocalStorageService } from './localStorage.service';
import { EasyEatHqService } from './ee-hq.service';
import {
  DUES_LOCK_STORAGE_KEY,
  DUES_WIRE_KEYS,
  DuesSegment,
} from '../enums/dues-lock.enum';

export interface DuesLockState {
  isPaymentOverdue: boolean;
  isBlockedPartially: boolean;
  pendingAmount: string | null;
  invoiceLink: string | null;
  blocked: string[];
}

const UNLOCKED: DuesLockState = {
  isPaymentOverdue: false,
  isBlockedPartially: false,
  pendingAmount: null,
  invoiceLink: null,
  blocked: [],
};

/**
 * POS-18027 — single source of truth for the dues partial lock.
 *
 * Resolved once after login (see AppComponent.initUser) and persisted so route
 * guards can answer synchronously. Nothing outside this service should read
 * `blockedDuesPending` or the storage key directly.
 *
 * Every failure path returns unlocked: a malformed payload must never lock a
 * restaurant that has actually paid.
 */
@Injectable({
  providedIn: 'root',
})
export class DuesLockService {
  private state: DuesLockState = { ...UNLOCKED };
  private readonly stateSubject = new BehaviorSubject<DuesLockState>({
    ...UNLOCKED,
  });

  /* Emits whenever the lock is re-resolved. The overdue page listens so a
     restaurant that has just paid isn't stranded on it after a refresh. */
  public readonly isActive$: Observable<boolean> = this.stateSubject
    .asObservable()
    .pipe(map((state) => state.isPaymentOverdue && state.isBlockedPartially));

  constructor(
    private _api: ApiService,
    private _localStorage: LocalStorageService,
    private _hqService: EasyEatHqService
  ) {
    this.state = this.readPersistedState();
    this.stateSubject.next(this.state);
  }

  /**
   * Resolve the lock once, at bootstrap, before the first guarded navigation.
   *
   * Deliberately not piggybacked on SubscriptionBannerComponent: that mounts
   * after the shell renders and is skipped entirely for HQ users, which would
   * let a deep link to /reports beat the fetch.
   */
  async resolve(restaurantId: string): Promise<void> {
    if (!restaurantId || this._hqService.isHqUser()) {
      return;
    }
    try {
      const payload = {
        rest_id: restaurantId,
        fields: { currentPlan: 1, blockedDuesPending: 1 },
      };
      const response: any = await this._api
        .fetchRestaurantDetailsWithPayload(payload)
        .toPromise();
      if ((response || {}).statusCode === 200) {
        this.setFromRestaurantDetails((response || {}).data);
      }
    } catch (error) {
      console.warn('POS-18027: dues lock fetch failed, failing open', error);
    }
  }

  setFromRestaurantDetails(data: any): void {
    /* No currentPlan means the payload is unusable, not that the restaurant has
       paid. Keep the previous state rather than emitting a false "unlocked",
       which would navigate anyone sitting on the overdue page away from it. */
    if (!(data || {}).currentPlan) {
      console.warn('POS-18027: restaurant details returned no currentPlan, keeping previous state');
      return;
    }
    this.state = this.normalise(
      (data || {}).currentPlan,
      (data || {}).blockedDuesPending
    );
    this._localStorage.set(DUES_LOCK_STORAGE_KEY, JSON.stringify(this.state));
    this.stateSubject.next(this.state);
  }

  clear(): void {
    this.state = { ...UNLOCKED };
    this._localStorage.remove(DUES_LOCK_STORAGE_KEY);
    this.stateSubject.next(this.state);
  }

  /* The gate. Both flags must be set before `blockedDuesPending` means anything. */
  isActive(): boolean {
    return this.state.isPaymentOverdue && this.state.isBlockedPartially;
  }

  /* Restaurant context only. HQ is never redirected — it filters outlets instead. */
  isSegmentLocked(segment: DuesSegment): boolean {
    if (this._hqService.isHqUser()) {
      return false;
    }
    if (!this.isActive()) {
      return false;
    }
    const key = (DUES_WIRE_KEYS[segment] || {}).restaurant;
    return !!key && this.state.blocked.includes(key);
  }

  /* HQ context. `outlet` is an entry from ee_outlets / staff_centre_outlets,
     carrying the `duesLock` object built by the backend. */
  isOutletLocked(outlet: any, segment: DuesSegment): boolean {
    const lock = (outlet || {}).duesLock;
    if (!lock) {
      return false;
    }
    if (!lock.isPaymentOverdue || !lock.isBlockedPartially) {
      return false;
    }
    const key = (DUES_WIRE_KEYS[segment] || {}).hq;
    return !!key && Array.isArray(lock.blocked) && lock.blocked.includes(key);
  }

  getLockInfo(): { pendingAmount: string | null; invoiceLink: string | null } {
    return {
      pendingAmount: this.state.pendingAmount,
      invoiceLink: this.state.invoiceLink,
    };
  }

  private normalise(currentPlan: any, blockedDuesPending: any): DuesLockState {
    try {
      const plan = currentPlan || {};
      return {
        isPaymentOverdue: Number(plan.isPaymentOverdue) === 1,
        isBlockedPartially: Number(plan.isBlockedPartially) === 1,
        pendingAmount:
          plan.currentPaymentPendingAmount != null
            ? String(plan.currentPaymentPendingAmount)
            : null,
        invoiceLink: plan.invoiceLink || null,
        blocked: Array.isArray(blockedDuesPending)
          ? blockedDuesPending.filter((e) => typeof e === 'string')
          : [],
      };
    } catch (error) {
      console.warn('POS-18027: could not read dues lock state, failing open', error);
      return { ...UNLOCKED };
    }
  }

  private readPersistedState(): DuesLockState {
    try {
      const raw = this._localStorage.get(DUES_LOCK_STORAGE_KEY);
      if (!raw || raw === 'undefined' || raw === 'null') {
        return { ...UNLOCKED };
      }
      const parsed = JSON.parse(raw);
      return {
        ...UNLOCKED,
        ...parsed,
        blocked: Array.isArray(parsed.blocked) ? parsed.blocked : [],
      };
    } catch (error) {
      console.warn('POS-18027: could not parse persisted dues lock state', error);
      return { ...UNLOCKED };
    }
  }
}
